import React from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/Layout";
import Disclaimer from "@/components/Disclaimer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Link2, AlertTriangle, Info } from "lucide-react";

export default function About() {
  return (
    <Layout>
      <section className="max-w-4xl mx-auto px-6 py-16" data-testid="about-page">
        <div className="mb-12">
          <div className="text-[10px] uppercase tracking-[0.24em] font-bold text-[var(--rg-sage)] mb-3">Our Story</div>
          <h1 className="font-heading font-extrabold text-4xl sm:text-5xl text-[var(--rg-text-strong)] mb-4">
            Built for Independent Shopify Merchants
          </h1>
        </div>

        {/* Section 1 — The Problem */}
        <div className="space-y-3 mb-12" data-testid="about-section-problem">
          <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)]">The compliance gap</h2>
          <p className="text-[var(--rg-text)] leading-relaxed">
            After the 2018 South Dakota v. Wayfair Supreme Court decision, every U.S. state with a
            sales tax adopted economic nexus rules. Overnight, selling online into other states
            created potential tax registration obligations that most small merchants had no idea
            existed.
          </p>
          <p className="text-[var(--rg-text)] leading-relaxed">
            At the same time, state consumer privacy laws began passing across the country &mdash;
            California, Texas, Colorado, Virginia, and more &mdash; each with different thresholds,
            rights, and enforcement priorities.
          </p>
          <p className="text-[var(--rg-text)] leading-relaxed">
            Large retailers hired compliance teams and legal counsel to navigate this. Independent
            Shopify merchants had Google.
          </p>
        </div>

        {/* Section 2 — What we built */}
        <div className="space-y-3 mb-12" data-testid="about-section-builtwhat">
          <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)]">Clarity, not complexity</h2>
          <p className="text-[var(--rg-text)] leading-relaxed">
            Regulens was built to give independent Shopify merchants the same compliance visibility
            that large retailers get from expensive legal teams &mdash; at a fraction of the cost,
            in plain English, with every finding traced to an official government source.
          </p>
          <p className="text-[var(--rg-text)] leading-relaxed">
            We are not lawyers. Regulens does not provide legal advice and never will. What we
            provide is a structured, informational readiness check that helps you understand where
            to focus your attention before you speak to a professional.
          </p>
        </div>

        {/* Section 3 — Our data */}
        <div className="space-y-3 mb-12" data-testid="about-section-data">
          <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)]">How we maintain accuracy</h2>
          <p className="text-[var(--rg-text)] leading-relaxed">
            Every data point in Regulens traces to an official government source &mdash; state
            revenue departments for sales tax, state attorney general offices for privacy law. We
            review high-priority jurisdictions weekly and update our database whenever laws change.
          </p>
          <p className="text-[var(--rg-text)] leading-relaxed">
            Our Data Verification dashboard shows the live status of all covered jurisdictions including
            last-verified dates and clickable source links.
          </p>
          <div className="pt-2">
            <Link to="/data-verification">
              <Button className="bg-[var(--rg-sage)] text-[var(--rg-on-sage)] hover:bg-[var(--rg-sage-hover)] font-semibold" data-testid="about-view-data">
                View Data Verification <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Section 4 — Our commitment */}
        <div className="mb-12" data-testid="about-section-commitment">
          <h2 className="font-heading font-bold text-2xl text-[var(--rg-text-strong)] mb-4">What we will always do</h2>
          <div className="grid sm:grid-cols-3 gap-4">
            <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5" data-testid="about-card-cite">
              <Link2 className="w-6 h-6 text-[var(--rg-sage)] mb-2" />
              <div className="font-heading font-bold text-[var(--rg-text-strong)] mb-1">Cite every source</div>
              <p className="text-xs text-[var(--rg-text)] leading-relaxed">
                Every finding links to the official government URL. You can verify anything we tell
                you.
              </p>
            </div>
            <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5" data-testid="about-card-limits">
              <AlertTriangle className="w-6 h-6 text-[var(--rg-amber)] mb-2" />
              <div className="font-heading font-bold text-[var(--rg-text-strong)] mb-1">Flag our limits</div>
              <p className="text-xs text-[var(--rg-text)] leading-relaxed">
                We tell you clearly what we do not cover. Federal laws, industry rules, and
                municipal regulations are explicitly outside our scope.
              </p>
            </div>
            <div className="rounded-md border border-[var(--rg-border)] bg-[var(--rg-surface)] p-5" data-testid="about-card-informational">
              <Info className="w-6 h-6 text-[var(--rg-sage)] mb-2" />
              <div className="font-heading font-bold text-[var(--rg-text-strong)] mb-1">Stay informational</div>
              <p className="text-xs text-[var(--rg-text)] leading-relaxed">
                We will never tell you that you are legally required to do something. We surface
                information. You and your advisors make decisions.
              </p>
            </div>
          </div>
        </div>

        <Disclaimer />
      </section>
    </Layout>
  );
}
